import { useRef } from "react";

import { IconButton } from "../IconButton";
import { Modal } from "../Modal";
import { Spinner } from "../Spinner";
import {
  AnnotationValidationReviewContent,
  type AnnotationValidationIssueLocation,
} from "./AnnotationSetValidationAction";
import { annotationStudioText } from "./messages";
import type {
  AnnotationSetValidationIssue,
  AnnotationSetValidationState,
  AnnotationStudioLocale,
  AnnotationTarget,
} from "./types";

export type AnnotationValidationReviewMode = "exit" | "navigation";

export interface AnnotationValidationReviewModalProps {
  readonly open: boolean;
  readonly mode: AnnotationValidationReviewMode;
  readonly state: AnnotationSetValidationState;
  readonly locale: AnnotationStudioLocale;
  readonly targets: readonly AnnotationTarget[];
  readonly summaryLocation: string | null;
  readonly issueLocations: ReadonlyMap<string, AnnotationValidationIssueLocation>;
  readonly currentFrameId: string | null;
  readonly materializingFrameId?: string | null;
  readonly hasUnsavedAnnotationChanges: boolean;
  readonly hasAnnotationContent: boolean;
  readonly overridePending: boolean;
  readonly onOpenIssue: (
    issue: AnnotationSetValidationIssue,
    frameId: string,
  ) => boolean | Promise<boolean>;
  readonly onOpenSummary: (() => void) | null;
  readonly onCancel: () => void;
  readonly onContinueAnyway: () => void;
}

/** Blocking validation decision surface. The command-bar popup remains inspection-only. */
export function AnnotationValidationReviewModal({
  open,
  mode,
  state,
  locale,
  targets,
  summaryLocation,
  issueLocations,
  currentFrameId,
  materializingFrameId = null,
  hasUnsavedAnnotationChanges,
  hasAnnotationContent,
  overridePending,
  onOpenIssue,
  onOpenSummary,
  onCancel,
  onContinueAnyway,
}: AnnotationValidationReviewModalProps) {
  // Modal은 open=false 뒤에도 exit fade 동안 children을 유지한다. 그 사이 parent의
  // pending request가 정리돼 mode가 fallback으로 바뀌어도 사용자가 누른 action 문구는
  // 닫힘 완료까지 그대로 보여야 한다.
  const visibleActionRef = useRef({ mode, overridePending });
  if (open) visibleActionRef.current = { mode, overridePending };
  const visibleAction = open ? { mode, overridePending } : visibleActionRef.current;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      closeOnEscape={open && !visibleAction.overridePending}
      closeOnBackdropClick={open && !visibleAction.overridePending}
      size="md"
    >
      <Modal.Header divider={false}>
        <Modal.Header.Start>
          <Modal.Header.Title>
            {annotationStudioText("annotationSetCheck", locale)}
          </Modal.Header.Title>
        </Modal.Header.Start>
      </Modal.Header>
      <Modal.Body tabIndex={-1}>
        <AnnotationValidationReviewContent
          state={state}
          locale={locale}
          targets={targets}
          onOpenIssue={onOpenIssue}
          onOpenSummary={onOpenSummary}
          onNavigate={onCancel}
          summaryLocation={summaryLocation}
          issueLocations={issueLocations}
          currentFrameId={currentFrameId}
          materializingFrameId={materializingFrameId}
          hasUnsavedAnnotationChanges={hasUnsavedAnnotationChanges}
          hasAnnotationContent={hasAnnotationContent}
        />
      </Modal.Body>
      <Modal.Footer divider={false}>
        <IconButton
          data-validation-review-cancel=""
          variant="solid"
          text={annotationStudioText("continueEditing", locale)}
          disabled={!open || visibleAction.overridePending}
          onClick={onCancel}
        />
        <IconButton
          data-validation-review-continue-anyway=""
          {...(visibleAction.mode === "exit" ? { "data-save-and-exit-anyway": "" } : {})}
          variant="surface"
          text={annotationStudioText(
            visibleAction.mode === "exit"
              ? visibleAction.overridePending
                ? "savingAndExit"
                : "saveAndExitAnyway"
              : "continueAnyway",
            locale,
          )}
          disabled={!open || visibleAction.overridePending}
          onClick={onContinueAnyway}
        >
          {visibleAction.overridePending ? (
            <Spinner label={annotationStudioText("savingAndExit", locale)} size="sm" />
          ) : null}
        </IconButton>
      </Modal.Footer>
    </Modal>
  );
}
